import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { useState } from "react";
import { Button, Dialog, Modal, ModalOverlay } from "react-aria-components";
import { toast } from "sonner";
import { cx } from "@/lib/cx";
import { inputClass } from "@/lib/forms";
import type { Model } from "@/modules/plus/plus.schema";
import { atoms } from "@/state";

// Module-local atom controlling the dialog's open state. Lives outside the
// ContributionsAtoms class because no other surface needs to drive it — the
// toolbar's Add Model button is the only opener.
export const isAddModelOpenAtom = atom<boolean>(false);

interface FormState {
  model: string;
  series: string;
  description: string;
  photo_url: string;
  product_page: string;
}

const EMPTY: FormState = {
  model: "",
  series: "",
  description: "",
  photo_url: "",
  product_page: "",
};

const labelClass = "text-xs font-medium text-gray-700 dark:text-gray-300";

export const AddModelDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useAtom(isAddModelOpenAtom);
  const saveMutation = useAtomValue(atoms.data.saveModel);
  const seriesNames = useAtomValue(atoms.suggestions.seriesNames);
  const [form, setForm] = useState<FormState>(EMPTY);

  const close = () => {
    setForm(EMPTY);
    setIsOpen(false);
  };

  const onSubmit = async () => {
    const trimmed = {
      model: form.model.trim(),
      series: form.series.trim(),
      description: form.description.trim(),
      photo_url: form.photo_url.trim(),
      product_page: form.product_page.trim(),
    };
    if (!trimmed.model || !trimmed.description) {
      toast.error("Model code and description are required");
      return;
    }
    const payload: Model = {
      model: trimmed.model,
      series: trimmed.series || trimmed.model,
      description: trimmed.description,
      photo_url: trimmed.photo_url,
      photo_match: "user_added",
      product_page: trimmed.product_page,
    };
    try {
      await saveMutation.mutateAsync(payload);
      toast.success(`Added ${trimmed.model}`);
      close();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    }
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      isDismissable
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <Modal className="w-full max-w-md rounded-2xl bg-white shadow-xl outline-none dark:bg-gray-900">
        <Dialog className="flex flex-col gap-4 p-6 outline-none">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold dark:text-gray-100">Add a model</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Saved to the shared overlay — visible to everyone immediately.
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="add-model-code" className={labelClass}>
              Model code *
            </label>
            <input
              id="add-model-code"
              autoFocus
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder="e.g. 7500X"
              className={cx(inputClass, "font-mono")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="add-model-series" className={labelClass}>
              Series name
            </label>
            <input
              id="add-model-series"
              list="add-model-series-list"
              value={form.series}
              onChange={(e) => setForm({ ...form, series: e.target.value })}
              placeholder="e.g. Model Series 7500"
              className={inputClass}
            />
            <datalist id="add-model-series-list">
              {seriesNames.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="add-model-desc" className={labelClass}>
              Description *
            </label>
            <input
              id="add-model-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Pattern Diffuser, Extra Long"
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="add-model-photo" className={labelClass}>
              Photo URL
            </label>
            <input
              id="add-model-photo"
              type="url"
              value={form.photo_url}
              onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
              placeholder="https://…"
              className={inputClass}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="add-model-page" className={labelClass}>
              Product page URL
            </label>
            <input
              id="add-model-page"
              type="url"
              value={form.product_page}
              onChange={(e) => setForm({ ...form, product_page: e.target.value })}
              placeholder="https://nailor.com/…"
              className={inputClass}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              onPress={close}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              isDisabled={saveMutation.isPending}
              onPress={onSubmit}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

// Helper hook for the toolbar button — returns a zero-arg callback.
export const useOpenAddModel = (): (() => void) => {
  const set = useSetAtom(isAddModelOpenAtom);
  return () => set(true);
};
